import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import {
  FaUser,
  FaEnvelope,
  FaLock,
  FaPhone,
  FaBuilding,
  FaArrowRight,
  FaArrowLeft,
  FaTimes,
  FaEye,
  FaEyeSlash,
} from "react-icons/fa";
import Api from "../axiosConfig";

const SignUp = () => {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [isLoading, setIsLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
    address: "",
    password: "",
    passwordConfirm: "",
    companyName: "",
    accountType: "other",
  });

  const [showPassword, setShowPassword] = useState(false);
  const [showPasswordConfirm, setShowPasswordConfirm] = useState(false);
  const [passwordErrors, setPasswordErrors] = useState([]);
  const [passwordStrength, setPasswordStrength] = useState({
    score: 0, // 0: none, 1: poor, 2: weak, 3: medium, 4: strong
    label: "",
    color: "",
  });
  const [isValid, setIsValid] = useState(false);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    const newFormData = {
      ...formData,
      [name]: value,
    };
    setFormData(newFormData);

    // Validate password on change
    if (name === "password" || name === "passwordConfirm") {
      const password = name === "password" ? value : formData.password;
      const confirmation =
        name === "passwordConfirm" ? value : newFormData.passwordConfirm;
      validatePassword(password);

      // Update validity based on both password and confirmation
      const errors = validatePassword(password);
      const passwordsMatch = password === confirmation;
      setIsValid(errors.length === 0 && passwordsMatch && password.length > 0);
    }
  };

  const nextStep = () => {
    setStep(step + 1);
  };

  const prevStep = () => {
    setStep(step - 1);
  };

  const calculatePasswordStrength = (password) => {
    let score = 0;

    // Length check
    if (password.length >= 8) score += 1;
    if (password.length >= 12) score += 1;

    // Character variety checks
    if (/[A-Z]/.test(password)) score += 1;
    if (/[0-9]/.test(password)) score += 1;
    if (/[!@#$%^&]/.test(password)) score += 1;
    if (/[a-z]/.test(password)) score += 1;

    // Normalize score to 0-4 range
    score = Math.min(4, Math.floor(score / 1.5));

    const strengthMap = {
      0: { label: "Too Short", color: "bg-gray-200" },
      1: { label: "Poor", color: "bg-red-500" },
      2: { label: "Weak", color: "bg-orange-500" },
      3: { label: "Medium", color: "bg-yellow-500" },
      4: { label: "Strong", color: "bg-green-500" },
    };

    return {
      score,
      label: strengthMap[score].label,
      color: strengthMap[score].color,
    };
  };

  const validatePassword = (password) => {
    const errors = [];
    const pattern = new RegExp("^(?=.*[A-Z])(?=.*[!@#$%^&])(?=.*[0-9]).{8,}$");

    if (!pattern.test(password)) {
      if (password.length < 8)
        errors.push("Password must be at least 8 characters long");
      if (!/[A-Z]/.test(password))
        errors.push("Password must contain at least one uppercase letter");
      if (!/[!@#$%^&]/.test(password))
        errors.push(
          "Password must contain at least one special character (!@#$%^&)"
        );
      if (!/[0-9]/.test(password))
        errors.push("Password must contain at least one number");
    }

    // Update password strength whenever validation occurs
    const strength = calculatePasswordStrength(password);
    setPasswordStrength(strength);

    // Update overall validity
    setIsValid(errors.length === 0 && password.length > 0);

    return errors;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (step !== 3) {
      nextStep();
      return;
    }

    const errors = validatePassword(formData.password);
    setPasswordErrors(errors);

    if (errors.length > 0) {
      errors.forEach((error) => toast.error(error));
      return;
    }

    if (formData.password !== formData.passwordConfirm) {
      toast.error("Passwords do not match");
      return;
    }

    try {
      setIsLoading(true);

      // Prepare registration data
      const registrationData = {
        email: formData.email,
        firstName: formData.firstName,
        lastName: formData.lastName,
        phone: formData.phone,
        password: formData.password,
        address: formData.address,
        accountType: formData.accountType,
        roleId: "680b684bac470dd4183ddf92", // Using the same roleId from your example
      };

      const response = await Api.post("/v1/auth/register", registrationData);

      if (response.data) {
        toast.success(
          "🎉 Registration successful! Please check your email for verification.",
          {
            position: "top-center",
            autoClose: 5000,
            hideProgressBar: false,
            closeOnClick: true,
            pauseOnHover: true,
            draggable: true,
            progress: undefined,
          }
        );
        setShowModal(true);
      }
    } catch (error) {
      // Extract error message
      let errorMessage = "Registration failed. Please try again.";

      if (error.response) {
        // Server responded with an error
        if (error.response.status === 409) {
          errorMessage =
            error.response.data?.info ||
            error.response.data?.message ||
            "This information is already registered";
        } else {
          errorMessage =
            error.response.data?.message ||
            error.response.data?.error ||
            "Server error. Please try again.";
        }
      } else if (error.request) {
        // Request was made but no response
        errorMessage =
          "Unable to reach the server. Please check your connection.";
      } else {
        // Something else went wrong
        errorMessage = error.message || "An unexpected error occurred.";
      }

      // Show error toast
      toast.error("❌ " + errorMessage, {
        position: "top-center",
        autoClose: 7000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
      });

      // Log error for debugging
      console.error("Registration error:", {
        status: error.response?.status,
        data: error.response?.data,
        message: error.message,
      });
    } finally {
      setIsLoading(false);
    }
  };

  const renderProgressBar = () => (
    <div className="mb-8">
      <div className="flex justify-between items-center text-xs sm:text-sm px-2">
        <div
          className={`text-center ${
            step >= 1 ? "text-primary-dark font-medium" : "text-gray-400"
          }`}
        >
          <span className="hidden sm:inline">Personal Info</span>
          <span className="sm:hidden">Personal</span>
        </div>
        <div
          className={`text-center ${
            step >= 2 ? "text-primary-dark font-medium" : "text-gray-400"
          }`}
        >
          <span className="hidden sm:inline">Address</span>
          <span className="sm:hidden">Address</span>
        </div>
        <div
          className={`text-center ${
            step >= 3 ? "text-primary-dark font-medium" : "text-gray-400"
          }`}
        >
          <span className="hidden sm:inline">Security</span>
          <span className="sm:hidden">Security</span>
        </div>
      </div>
      <div className="mt-2 h-2 flex rounded-full bg-gray-200 mx-2">
        <div
          className="bg-primary-dark rounded-full transition-all duration-300"
          style={{ width: `${(step / 3) * 100}%` }}
        />
      </div>
    </div>
  );

  const renderStep1 = () => (
    <>
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        <div>
          <label
            htmlFor="firstName"
            className="block text-sm font-medium text-gray-700"
          >
            First Name
          </label>
          <div className="mt-1 relative rounded-md -sm">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <FaUser className="h-5 w-5 text-gray-400" />
            </div>
            <input
              type="text"
              name="firstName"
              id="firstName"
              required
              className="block w-full pl-10 pr-3 py-2 border border-gray-300 border-solid rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:ring-primary focus:border-primary sm:text-sm"
              placeholder="First Name"
              value={formData.firstName}
              onChange={handleChange}
            />
          </div>
        </div>

        <div>
          <label
            htmlFor="lastName"
            className="block text-sm font-medium text-gray-700"
          >
            Last Name
          </label>
          <div className="mt-1 relative rounded-md -sm">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <FaUser className="h-5 w-5 text-gray-400" />
            </div>
            <input
              type="text"
              name="lastName"
              id="lastName"
              required
              className="block w-full pl-10 pr-3 py-2 border border-gray-300 border-solid rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:ring-primary focus:border-primary sm:text-sm"
              placeholder="Last Name"
              value={formData.lastName}
              onChange={handleChange}
            />
          </div>
        </div>

        <div>
          <label
            htmlFor="email"
            className="block text-sm font-medium text-gray-700"
          >
            Email
          </label>
          <div className="mt-1 relative rounded-md -sm">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <FaEnvelope className="h-5 w-5 text-gray-400" />
            </div>
            <input
              type="email"
              name="email"
              id="email"
              required
              className="block w-full pl-10 pr-3 py-2 border border-gray-300 border-solid rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:ring-primary focus:border-primary sm:text-sm"
              placeholder="Email address"
              value={formData.email}
              onChange={handleChange}
            />
          </div>
        </div>

        <div>
          <label
            htmlFor="phone"
            className="block text-sm font-medium text-gray-700"
          >
            Phone
          </label>
          <div className="mt-1 relative rounded-md -sm">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <FaPhone className="h-5 w-5 text-gray-400" />
            </div>
            <input
              type="tel"
              name="phone"
              id="phone"
              required
              className="block w-full pl-10 pr-3 py-2 border border-gray-300 border-solid rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:ring-primary focus:border-primary sm:text-sm"
              placeholder="Phone number"
              value={formData.phone}
              onChange={handleChange}
            />
          </div>
        </div>
      </div>
    </>
  );

  const renderStep2 = () => (
    <div className="space-y-6">
      <div>
        <label
          htmlFor="address"
          className="block text-sm font-medium text-gray-700"
        >
          Address
        </label>
        <div className="mt-1 relative rounded-md -sm">
          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
            <FaBuilding className="h-5 w-5 text-gray-400" />
          </div>
          <input
            type="text"
            name="address"
            id="address"
            required
            className="block w-full pl-10 pr-3 py-2 border border-gray-300 border-solid rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:ring-primary focus:border-primary sm:text-sm"
            placeholder="Enter your address"
            value={formData.address}
            onChange={handleChange}
          />
        </div>
      </div>

      <div>
        <label
          htmlFor="accountType"
          className="block text-sm font-medium text-gray-700"
        >
          Account Type
        </label>
        <select
          id="accountType"
          name="accountType"
          required
          className="mt-1 block w-full pl-3 pr-10 py-2 text-base border border-gray-300 border-solid focus:outline-none focus:ring-primary focus:border-primary sm:text-sm rounded-md"
          value={formData.accountType}
          onChange={handleChange}
        >
          <option value="health_care_provider">Healthcare Provider</option>
          <option value="medical_laboratory">Medical Laboratory</option>
          <option value="pharmacy">Pharmacy</option>
          <option value="hospital_clinic">Hospital/Clinic</option>
        </select>
      </div>
    </div>
  );

  const renderStep3 = () => (
    <>
      <div className="space-y-6">
        <div>
          <label
            htmlFor="password"
            className="block text-sm font-medium text-gray-700"
          >
            Password
          </label>
          <div className="mt-1 relative rounded-md -sm">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <FaLock className="h-5 w-5 text-gray-400" />
            </div>
            <input
              type={showPassword ? "text" : "password"}
              name="password"
              id="password"
              required
              className={`block w-full pl-10 pr-10 py-2 border ${
                formData.password
                  ? passwordStrength.score >= 3
                    ? "border-green-500 border-[1px] border-solid"
                    : passwordStrength.score >= 2
                    ? "border-yellow-500 border-[1px] border-solid"
                    : "border-red-500"
                  : "border-gray-300 border-[1px] border-solid"
              } rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:ring-primary focus:border-primary sm:text-sm transition-colors duration-200`}
              placeholder="Create a password"
              value={formData.password}
              onChange={handleChange}
            />
            <button
              type="button"
              className="absolute inset-y-0 right-0 pr-3 flex items-center"
              onClick={() => setShowPassword(!showPassword)}
            >
              {showPassword ? (
                <FaEyeSlash className="h-5 w-5 text-gray-400 hover:text-gray-500" />
              ) : (
                <FaEye className="h-5 w-5 text-gray-400 hover:text-gray-500" />
              )}
            </button>
          </div>
        </div>

        <div>
          <label
            htmlFor="passwordConfirm"
            className="block text-sm font-medium text-gray-700"
          >
            Confirm Password
          </label>
          <div className="mt-1 relative rounded-md -sm">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <FaLock className="h-5 w-5 text-gray-400" />
            </div>
            <input
              type={showPasswordConfirm ? "text" : "password"}
              name="passwordConfirm"
              id="passwordConfirm"
              required
              className={`block w-full pl-10 pr-10 py-2 border ${
                formData.passwordConfirm
                  ? formData.password === formData.passwordConfirm
                    ? "border-green-500 border-[1px] border-solid"
                    : "border-red-500"
                  : "border-gray-300 border-[1px] border-solid"
              } rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:ring-primary focus:border-primary sm:text-sm transition-colors duration-200`}
              placeholder="Confirm your password"
              value={formData.passwordConfirm}
              onChange={handleChange}
            />
            <button
              type="button"
              className="absolute inset-y-0 right-0 pr-3 flex items-center"
              onClick={() => setShowPasswordConfirm(!showPasswordConfirm)}
            >
              {showPasswordConfirm ? (
                <FaEyeSlash className="h-5 w-5 text-gray-400 hover:text-gray-500" />
              ) : (
                <FaEye className="h-5 w-5 text-gray-400 hover:text-gray-500" />
              )}
            </button>
          </div>
        </div>

        {formData.password && (
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <div className="text-sm font-medium text-gray-900">
                Password Strength: {passwordStrength.label}
              </div>
            </div>
            <div className="h-2 w-full bg-gray-200 rounded-full overflow-hidden">
              <div
                className={`h-full ${passwordStrength.color} transition-all duration-300`}
                style={{ width: `${(passwordStrength.score / 4) * 100}%` }}
              />
            </div>
          </div>
        )}

        <div className="rounded-md bg-blue-50 p-4">
          <div className="flex">
            <div className="flex-shrink-0">
              <FaLock className="h-5 w-5 text-blue-400" aria-hidden="true" />
            </div>
            <div className="ml-3">
              <h3 className="text-sm font-medium text-blue-800">
                Password Requirements
              </h3>
              <div className="mt-2 text-sm text-blue-700">
                <ul className="list-disc space-y-1 pl-5">
                  <li>At least 8 characters long</li>
                  <li>At least one uppercase letter</li>
                  <li>At least one number</li>
                  <li>At least one special character (!@#$%^&)</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );

  const renderStepContent = () => {
    switch (step) {
      case 1:
        return renderStep1();
      case 2:
        return renderStep2();
      case 3:
        return renderStep3();
      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen relative flex flex-col justify-center bg-[url(]'/img/Website/IMPACTCars.png')] bg-cover py-12 sm:px-3 lg:px-8">
      {/* Background Overlay */}
      <div className="absolute inset-0 bg-primary-dark opacity-60"></div>

      {/* Content */}
      <div className="relative z-10">
        <div className="sm:mx-auto sm:w-full sm:max-w-md">
          <img
            className="mx-auto h-16 w-auto"
            src="/img/impact.webp"
            alt="Impact Logistics"
          />
          <h2 className="mt-6 text-center text-3xl font-extrabold text-white">
            Create Your Account
          </h2>
          <p className="mt-2 text-center text-sm text-gray-200">
            Join our medical logistics network
          </p>
        </div>

        <div className="mt-8 sm:mx-auto sm:w-full px-4 sm:px-9 lg:max-w-3xl">
          <div className="bg-white opacity-70 p-4 sm:p-10 rounded-lg">
            <div className="bg-white py-6 sm:py-8 px-4  rounded-lg sm:px-10">
              {renderProgressBar()}
              <form className="space-y-6" onSubmit={handleSubmit}>
                {renderStepContent()}

                <div className="flex flex-col sm:flex-row justify-between gap-3 sm:gap-4">
                  {step > 1 && (
                    <button
                      type="button"
                      onClick={prevStep}
                      disabled={isLoading}
                      className="order-1 sm:order-none flex-1 flex justify-center items-center gap-2 py-2.5 px-4 border border-gray-300 border-solid rounded-md -sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary transition-all duration-200 disabled:opacity-50"
                    >
                      <FaArrowLeft className="h-4 w-4" />
                      Back
                    </button>
                  )}
                  <button
                    type="submit"
                    disabled={isLoading}
                    className="order-0 sm:order-none flex-1 flex justify-center items-center gap-2 py-2.5 px-4 border border-transparent rounded-md -sm text-sm font-medium text-white bg-primary-dark hover:bg-[#001B3B] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary transition-all duration-200 disabled:opacity-50"
                  >
                    {isLoading ? (
                      <span className="flex items-center gap-2">
                        <svg
                          className="animate-spin h-5 w-5 text-white"
                          xmlns="http://www.w3.org/2000/svg"
                          fill="none"
                          viewBox="0 0 24 24"
                        >
                          <circle
                            className="opacity-25"
                            cx="12"
                            cy="12"
                            r="10"
                            stroke="currentColor"
                            strokeWidth="4"
                          ></circle>
                          <path
                            className="opacity-75"
                            fill="currentColor"
                            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                          ></path>
                        </svg>
                        Processing...
                      </span>
                    ) : (
                      <>
                        {step === 3 ? "Create Account" : "Next"}
                        <FaArrowRight className="h-4 w-4" />
                      </>
                    )}
                  </button>
                </div>
              </form>

              {step === 1 && (
                <div className="mt-6">
                  <div className="relative">
                    <div className="absolute inset-0 flex items-center">
                      <div className="w-full border-t border-gray-300 border-solid" />
                    </div>
                    <div className="relative flex justify-center text-sm">
                      <span className="px-2 bg-white text-gray-500">
                        Already have an account?
                      </span>
                    </div>
                  </div>

                  <div className="mt-6">
                    <Link
                      to="/auth/login"
                      className="w-full flex justify-center items-center gap-2 py-2.5 px-4 border border-transparent rounded-md -sm text-sm font-medium text-primary-dark bg-gray-50 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary transition-all duration-200"
                    >
                      Sign in to your account
                      <FaArrowRight className="h-4 w-4" />
                    </Link>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Verification Modal */}
        {showModal && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
            <div className="absolute inset-0 bg-black opacity-50"></div>
            <div className="relative bg-white rounded-lg p-8 max-w-md w-full">
              <button
                onClick={() => {
                  setShowModal(false);
                  navigate("/auth/login");
                }}
                className="absolute top-4 right-4 text-gray-500 hover:text-gray-700"
              >
                <FaTimes className="h-5 w-5" />
              </button>
              <div className="text-center">
                <FaEnvelope className="mx-auto h-12 w-12 text-primary-dark" />
                <h3 className="mt-4 text-xl font-semibold text-gray-900">
                  Verify your email
                </h3>
                <p className="mt-2 text-gray-600">
                  We've sent a verification link to{" "}
                  <span className="font-medium">{formData.email}</span>. Please
                  check your email and click the link to verify your account.
                </p>
                <div className="mt-6 flex flex-col gap-3">
                  <Link
                    to="/auth/login"
                    className=" justify-center flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-primary-dark hover:bg-[#001B3B] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary transition-all duration-200"
                  >
                    Go to Login
                    <FaArrowRight className="ml-2 h-4 w-4" />
                  </Link>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default SignUp;
